import React from 'react';
import { Card, Badge, Button } from '@shared/components/ui';
import { Play, Edit3, Trash2, Database, FileText, Power, History } from 'lucide-react';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '@shared/utils/formatRelativeTime';
import { normalizeBackupType } from '../utils/backupType';

interface BackupConfig {
  id: string;
  name: string;
  description?: string;
  type: string;
  agent_id: string;
  agent_name?: string;
  storage_destination_name?: string;
  config?: {
    // FILES type
    paths?: string[];
    // POSTGRESQL type - flat structure
    host?: string;
    port?: number;
    database?: string;
    databases?: string[];
    username?: string;
    use_pg_dump_all?: boolean;
    backup_globals_only?: boolean;
  };
  is_enabled: boolean;
  run_missing_jobs_on_startup?: boolean;
  created_at: string;
  last_job_status?: string;
  last_job_completed_at?: string;
}

interface BackupConfigItemProps {
  config: BackupConfig;
  onEdit: (config: BackupConfig) => void;
  onDelete: (config: BackupConfig) => void;
  onTrigger: (config: BackupConfig) => void;
  onToggleEnable: (config: BackupConfig) => void;
  onClick: (config: BackupConfig) => void;
  onViewSnapshots?: (config: BackupConfig) => void;
}

export const BackupConfigItem: React.FC<BackupConfigItemProps> = ({
  config,
  onEdit,
  onDelete,
  onTrigger,
  onToggleEnable,
  onClick,
  onViewSnapshots,
}) => {
  const { t } = useTranslation('backups');

  /** Last-run badge: labels differ from the shared status vocabulary. */
  const lastJobStatusKeys: Record<string, string> = {
    COMPLETED: 'completed',
    FAILED: 'failed',
    RUNNING: 'running',
    PENDING: 'pending',
    CANCELLED: 'cancelled',
  };
  const backupType = normalizeBackupType(config.type);
  return (
    <Card className="p-0 overflow-hidden group" data-testid="backup-config-item">
      <div className="flex items-stretch min-h-[100px]">
        <div
          className={`w-1.5 ${config.is_enabled ? 'bg-brand-500' : 'bg-surface-border-light dark:bg-surface-border-dark'}`}
        />
        <div className="flex-1 p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <button
            type="button"
            aria-label={t('configItem.openSnapshots', { name: config.name })}
            className="flex items-center space-x-4 flex-1 min-w-0 cursor-pointer text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            onClick={() => onClick(config)}
          >
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${
                backupType === 'POSTGRESQL'
                  ? 'bg-state-info-bg-light dark:bg-state-info-bg-dark text-state-info-light dark:text-state-info-dark'
                  : 'bg-state-warn-bg-light dark:bg-state-warn-bg-dark text-state-warn-light dark:text-state-warn-dark'
              }`}
            >
              {backupType === 'POSTGRESQL' ? <Database size={24} /> : <FileText size={24} />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="font-black text-slate-900 dark:text-white tracking-tight truncate">
                  {config.name}
                </h3>
                <Badge variant={config.is_enabled ? 'success' : 'neutral'}>
                  {config.is_enabled ? t('configItem.enabled') : t('configItem.disabled')}
                </Badge>
                {config.run_missing_jobs_on_startup && (
                  <Badge variant="info">{t('configItem.autoRunMissed')}</Badge>
                )}
              </div>
              <p className="text-xs font-semibold leading-relaxed text-text-subtle-light dark:text-text-subtle-dark mt-1">
                {t('configItem.agent')}{' '}
                <span className="text-slate-600 dark:text-slate-300">
                  {config.agent_name || t('configItem.unknown')}
                </span>
                <span className="mx-2">•</span>
                {t('configItem.type')}{' '}
                <span className="text-slate-600 dark:text-slate-300">
                  {backupType === 'POSTGRESQL'
                    ? t('filters.type.postgresql')
                    : backupType === 'FILES'
                      ? t('filters.type.files')
                      : config.type}
                </span>
                {config.storage_destination_name && (
                  <>
                    <span className="mx-2">•</span>
                    {t('configItem.vault')}{' '}
                    <span className="text-slate-600 dark:text-slate-300">
                      {config.storage_destination_name}
                    </span>
                  </>
                )}
                {config.last_job_completed_at && (
                  <>
                    <span className="mx-2">•</span>
                    {t('configItem.lastRun')}{' '}
                    <span className="text-slate-600 dark:text-slate-300">
                      {formatRelativeTime(config.last_job_completed_at)}
                    </span>
                  </>
                )}
              </p>
              {config.config && (
                <p className="text-xs font-semibold leading-relaxed text-text-subtle-light dark:text-text-subtle-dark mt-1">
                  {backupType === 'POSTGRESQL' ? (
                    <>
                      {t('configItem.database')}{' '}
                      <span className="text-slate-600 dark:text-slate-300">
                        {config.config.host || 'localhost'}
                        {config.config.port ? `:${config.config.port}` : ''}
                        {config.config.database ? `/${config.config.database}` : ''}
                        {config.config.databases && config.config.databases.length > 0 && (
                          <span className="text-text-muted-light dark:text-text-muted-dark ml-1">
                            ({config.config.databases.join(', ')})
                          </span>
                        )}
                      </span>
                    </>
                  ) : backupType === 'FILES' &&
                    config.config.paths &&
                    config.config.paths.length > 0 ? (
                    <>
                      {t('configItem.paths')}{' '}
                      <span className="normal-case tracking-normal text-slate-600 dark:text-slate-300 truncate max-w-md inline-block align-bottom">
                        {config.config.paths.join(', ')}
                      </span>
                    </>
                  ) : null}
                </p>
              )}
              {config.last_job_status && (
                <div className="mt-2">
                  <Badge variant={toneToBadgeVariant[getStatusTone(config.last_job_status)]}>
                    {lastJobStatusKeys[config.last_job_status]
                      ? t(`configItem.jobStatus.${lastJobStatusKeys[config.last_job_status]}`)
                      : config.last_job_status}
                  </Badge>
                </div>
              )}
            </div>
          </button>

          <div className="flex items-center space-x-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              aria-label={
                config.is_enabled ? t('configItem.actions.disable') : t('configItem.actions.enable')
              }
              className={`!min-h-11 min-w-11 ${
                config.is_enabled
                  ? 'text-state-warn-light dark:text-state-warn-dark hover:bg-state-warn-bg-light dark:hover:bg-state-warn-bg-dark'
                  : 'text-state-ok-light dark:text-state-ok-dark hover:bg-state-ok-bg-light dark:hover:bg-state-ok-bg-dark'
              }`}
              onClick={() => onToggleEnable(config)}
              title={
                config.is_enabled ? t('configItem.actions.disable') : t('configItem.actions.enable')
              }
            >
              <Power size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('configItem.actions.runNow')}
              onClick={() => onTrigger(config)}
              className="!min-h-11 min-w-11 text-state-ok-light dark:text-state-ok-dark hover:bg-state-ok-bg-light dark:hover:bg-state-ok-bg-dark"
              title={t('configItem.actions.runNow')}
            >
              <Play size={16} fill="currentColor" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              aria-label={t('configItem.actions.edit')}
              className="!min-h-11 min-w-11"
              onClick={() => onEdit(config)}
              title={t('configItem.actions.edit')}
            >
              <Edit3 size={16} />
            </Button>
            {onViewSnapshots && (
              <Button
                variant="secondary"
                size="sm"
                aria-label={t('configItem.actions.viewSnapshots')}
                className="!min-h-11 min-w-11 text-brand-600 dark:text-brand-400 hover:bg-accent-soft-light dark:hover:bg-accent-soft-dark"
                onClick={() => onViewSnapshots(config)}
                title={t('configItem.actions.viewSnapshots')}
              >
                <History size={16} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('configItem.actions.delete')}
              onClick={() => onDelete(config)}
              className="!min-h-11 min-w-11 text-state-danger-light dark:text-state-danger-dark hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark"
              title={t('configItem.actions.delete')}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
